import {
  StartGuideReadinessTaskId,
  StartGuideState,
  StartGuideStep,
} from "@/features/pathpick/features/start-guide/types";
import { PathPickProductCardViewSettings } from "@/features/pathpick/types";

// 저장 스키마 버전 — 구조가 바뀌면 올린다 (읽을 때 불일치하면 기본값으로 되돌린다).
export const START_GUIDE_STORAGE_KEY = "pathpick-start-guide";
export const START_GUIDE_STORAGE_VERSION = 2;

// 설정 태스크 구성 revision — 태스크가 추가/삭제되거나 완료 의미가 바뀌면 올린다.
// 올리면 저장돼 있던 completedTasks 를 버려 처음부터 다시 밟게 한다.
export const START_GUIDE_READINESS_REVISION = 1;

export const START_GUIDE_DEFAULT_STATE: StartGuideState = {
  version: START_GUIDE_STORAGE_VERSION,
  readinessRevision: START_GUIDE_READINESS_REVISION,
  // 판매 방식을 저장하기 전(hasSavedProfile: false)에는 선택 화면만 보이므로
  // 이 값은 쓰이지 않는다 — 라디오도 아무것도 고르지 않은 채로 시작한다.
  salesMode: "api",
  hasSavedProfile: false,
  smartStorePresence: null,
  isPreflightConfirmed: false,
  completedTasks: [],
  currentStep: "setup",
  // 어느 판매 방식에나 있는 화면이라 기본값으로 안전하다(진입할 때 흐름의 첫 화면으로 다시 정해진다).
  afterOrderPage: "order",
  freeTrialStartedAt: null,
};

// 스마트스토어 판매자센터 대시보드 (이미 개설한 경우)
export const SMART_STORE_SELLER_CENTER_URL =
  "https://sell.smartstore.naver.com/#/home/dashboard";

// 스마트스토어 개설 (아직 없는 경우)
export const SMART_STORE_SIGN_UP_URL = "https://sell.smartstore.naver.com/home";

// 상품판매권한 신청 (해외상품판매)
export const SMART_STORE_SELL_AUTH_URL =
  "https://sell.smartstore.naver.com/#/member/auth/sell";

// 판매자 정보 — 배송정보 > 주소록에서 해외 출고지를 등록한다.
export const SMART_STORE_SELLER_INFO_URL =
  "https://sell.smartstore.naver.com/#/seller/info";

// 전달서 §2가 지정한 파일명 그대로 둔다.
// 파일이 아직 없으면 PathPickStartGuideExampleImage 가 플레이스홀더로 대체 렌더한다.
export const START_GUIDE_IMAGES = {
  smartStoreApiSettings:
    "/images/pathpick-smart-store-api-settings-example.png",
  smartStoreRegistrationAdmin:
    "/images/pathpick-smart-store-registration-admin-example.png",
  smartStoreRegistrationProduct:
    "/images/pathpick-smart-store-registration-product-example.png",
  // 엑셀·KREAM 흐름의 업로드 안내 모달에서 쓰는 판매자센터 캡처.
  smartStoreProductListing: "/images/pathpick-smart-store-product-listing.png",
  kreamMandatoryProductDisclosure:
    "/images/pathpick-kream-mandatory-product-disclosure.png",
  kreamBulkBidding: "/images/pathpick-kream-bulk-bidding.png",
  celine: "/images/pathrade-home-celine-10l033dpv-black.jpg",
  gucci: "/images/pathrade-home-gucci-phone-pouch-875806fafv4.avif",
} as const;

// 태스크 id → 목록에 보여줄 제목 i18n 키.
export const READINESS_TASK_TITLE_KEYS: Record<
  StartGuideReadinessTaskId,
  string
> = {
  product_type: "path_pick_guide:readiness.task.product_type",
  overseas_permission: "path_pick_guide:readiness.task.overseas_permission",
  overseas_release: "path_pick_guide:readiness.task.overseas_release",
  api_setting: "path_pick_guide:readiness.task.api_setting",
  delivery_template: "path_pick_guide:readiness.task.delivery_template",
  buyer_info: "path_pick_guide:readiness.task.buyer_info",
};

// 상단 단계 탭 라벨 i18n 키.
export const START_GUIDE_STEP_LABEL_KEYS = {
  setup: "path_pick_guide:tabs.setup",
  product_tour: "path_pick_guide:tabs.product_tour",
  workbench: "path_pick_guide:tabs.workbench",
  after_order: "path_pick_guide:tabs.after_order",
  membership: "path_pick_guide:tabs.membership",
  faq: "path_pick_guide:tabs.faq",
  finish: "path_pick_guide:tabs.finish",
} as const;

// KREAM 만 쓰면 등록 신청이 없어 이 탭이 주문에서 시작한다 — 라벨도 그 흐름을 따른다.
export function getStartGuideStepLabelKey(
  step: StartGuideStep,
  isKreamOnly: boolean
) {
  if (step === "after_order" && isKreamOnly) {
    return "path_pick_guide:tabs.after_order_kream";
  }
  return START_GUIDE_STEP_LABEL_KEYS[step];
}

// 시작 가이드는 사용자의 카드 보기 설정(localStorage)과 무관하게 항상 같은 화면에서 시작한다 —
// 카드도 상세 모달도 비교를 켠 위아래 배치로 시작한다.
// 예외는 상품 찾기 안내 4/5 뿐이다. 그 단계는 보기 설정을 직접 바꿔 보는 곳이라 이 값을 시작값으로만
// 쓰고, 바뀐 값은 그 화면 state에만 남는다(실제 설정에는 저장하지 않는다).
export const START_GUIDE_CARD_VIEW: PathPickProductCardViewSettings = {
  showComparison: true,
  comparisonLayout: "stack",
};
